import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const multiDeviceSplaySkill: CodeoSkill = {
    name: SKILL_NAMES.multiDeviceSplay,
    kind: 'codeo',
    description:
        'Fan two or three device frames into an overlapping arrangement so one product reads as ' +
        'being everywhere at once — for cross-platform, "works on anything", or a launch lockup.',
    documentUsesSkill: check(
        (script, markup) =>
            /perspective/.test(markup) && /rotationY/.test(script) && repeatsAClass(markup),
    ),
    instructions: `# Multi-Device Splay

Two or three device frames fan out into an overlapping arrangement, each at a slightly different depth and angle, all showing the same product. It is the standard claim that something works everywhere, and it lives or dies on the arrangement: an evenly spaced row of devices is a stock image, while an overlapping splay with a clear front object reads as composed.

Establish one device as the hero. If all three are the same size at the same angle, the eye has nowhere to start.

## Markup

Author them overlapping, biggest and most central first in visual weight but *last* in DOM order so it paints on top.

\`\`\`html
<div id="splay" style="position: absolute; left: 0; right: 0; top: 200px; height: 700px; perspective: 1800px;">
  <div class="device" style="position: absolute; left: 380px; top: 90px; width: 420px; height: 520px; border-radius: 26px; padding: 12px; background: var(--l5-color-1, #101820); box-shadow: 0 40px 90px rgba(0,0,0,0.4);">
    <div style="width: 100%; height: 100%; border-radius: 16px; background: var(--l5-color-2, #38bdf8);"></div>
  </div>
  <div class="device" style="position: absolute; left: 1120px; top: 130px; width: 380px; height: 470px; border-radius: 26px; padding: 12px; background: var(--l5-color-1, #101820); box-shadow: 0 40px 90px rgba(0,0,0,0.4);">
    <div style="width: 100%; height: 100%; border-radius: 16px; background: var(--l5-color-2, #38bdf8);"></div>
  </div>
  <div class="device" id="device-hero" style="position: absolute; left: 700px; top: 0; width: 520px; height: 640px; border-radius: 30px; padding: 14px; background: var(--l5-color-1, #101820); box-shadow: 0 56px 120px rgba(0,0,0,0.5);">
    <div style="position: relative; width: 100%; height: 100%; border-radius: 18px; overflow: hidden; background: var(--l5-color-0, #f4f6f9);">
      <div id="hero-sheen" style="position: absolute; left: 0; right: 0; top: 0; height: 22%; background: linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0));"></div>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

They arrive stacked and fan outward, the hero settling last and straight while the others keep their tilt.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('.device', { x: 0, y: 60, opacity: 0, rotationY: 0, scale: 0.94 });
gsap.set('#hero-sheen', { opacity: 0.65 });

tl.to('.device', {
    y: 0,
    opacity: 1,
    scale: 1,
    duration: 0.7,
    ease: 'power3.out',
    stagger: 0.12,
}, 0.3);

// The fan: outer devices tip away, the hero stays square to the camera.
tl.to('.device:not(#device-hero)', { rotationY: 16, duration: 0.8, ease: 'power2.out' }, 0.5);
tl.to('#device-hero', { y: -14, duration: 0.8, ease: 'power3.out' }, 0.7);

// The hero screen's own life — a real element, so it can actually be tweened.
tl.to('#hero-sheen', { opacity: 0.9, duration: 1.6, ease: 'sine.inOut' }, 1.6);
tl.to('#hero-sheen', { opacity: 0.65, duration: 1.8, ease: 'sine.inOut' }, 3.2);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Angle**: tip the flanking devices *away* from the hero (positive on one side, negative on the other) so the arrangement reads as symmetric depth rather than a lean.
- **Depth by size**: 15–25% smaller for the flanking devices. Any less and the hero does not read as in front; any more and they look like different products.
- **Dive into the hero**: once splayed, moving into the hero's screen hands off to a detail — see \`${SKILL_NAMES.deviceScreenDive}\`.
- **Two devices**: often stronger than three. A phone and a laptop says "everywhere" as completely as three phones do.
- **Screens alive**: give the hero's screen a small amount of its own motion once the splay settles — a highlight band easing between roughly 0.65 and 0.9 opacity is enough, and it must be a real element (see Pitfalls). The flanking screens stay still so they do not compete.
- **Shadow depth**: the hero's shadow should be larger and softer than the others'. That single difference does more for the depth read than the scale does.

## Pitfalls

- Devices are transform-tweened on several channels, so none may carry a CSS \`transform\`; \`perspective\` belongs on the container.
- **A \`::before\`/\`::after\` cannot be a GSAP target.** Pseudo-elements are not in the DOM, so a selector like \`.screen:after\` matches nothing and the tween is silent — and because a pseudo-element in a selector can also be rejected outright by the browser's selector engine, the throw can take the whole timeline registration with it and leave the composition on a single static frame. Author any highlight you intend to animate as a real \`<div>\`.
- DOM order determines what paints on top. The hero must come last, whatever its position on screen.
- Don't rotate the hero. A front object square to the camera is what gives the arrangement its anchor.
- Keep the screens abstract unless the content is the point. Three legible interfaces at once is unreadable at video pace.
- Even spacing at equal size is the "identical card grid" failure in device form — vary size, offset and depth.
- Three is the maximum. A fourth device adds nothing to the claim and costs the composition its focus.
- Rasterizing three 3D-transformed subtrees with large soft shadows is expensive; keep each screen to a couple of elements.`,
};
